import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Button as AntdButton, Dropdown as AntdDropdown } from 'antd';

import {
	AiOutlineFileText,
	AiOutlineMore,
	AiFillFolderOpen,
	AiOutlinePlayCircle,
	AiOutlineDownload,
	AiOutlineFilePdf,
	AiOutlineHistory,
} from 'react-icons/ai';
import { FaRegMap } from 'react-icons/fa';

import { archiveProject, adminGetProjectFilesDowloadUrl, unsetDemoProject } from '@utils/api';
import { downloadProjectFiles } from '@utils/downloads';
import {
	DEFAULT_PROJECT_VIEW_MODE,
	IMAGE_MODE,
	MODEL_VIEW_MODE_ENABLED,
	PROJECT_VIEW_MODE,
} from '@utils/constants';
import { buildProjectUrl, projectHomeUrl } from '@utils/projectPath';

import ReportModal from './ReportModal';
import DetailsModal from './DetailsModal';
import OdmPostprocessModal from './OdmPostprocessModal';
import SagemakerDownloadModal from './SagemakerDownloadModal';
import DemoProjectModal from './DemoProjectModal';
import EditModal from './EditModal';
import { useToast } from '@contexts/Toast.context';
import { useConfirm } from '@contexts/Confirm.context';

const DropdownMenu = ({ data }) => {
	const { addToast } = useToast();
	const { confirm } = useConfirm();
	const navigate = useNavigate();

	const { uuid, image_mode, demo_project, title } = data;

	const [showReport, setShowReport] = useState(false);
	const [showDetails, setShowDetails] = useState(null);
	const [showPostprocess, setShowPostprocess] = useState(false);
	const [showSagemaker, setShowSagemaker] = useState(false);
	const [showProjectDemo, setShowProjectDemo] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);

	const handleDelete = async () => {
		const userConfirmed = await confirm({
			title: 'Archive Project',
			message: (
				<>
					Are you sure you want to archive this project: <strong>{title}</strong>?
				</>
			),
		});

		if (userConfirmed) {
			try {
				await archiveProject(uuid);
				window.location.reload();
			} catch (err) {
				console.error('Failed to archive project', err);
				addToast({
					id: 'project-archive-error',
					bg: 'danger',
					title: 'Failed to archive project.',
				});
			}
		}
	};

	const handleUnsetDemo = async () => {
		const userConfirmed = await confirm({
			title: 'Unset Demo Project',
			message: (
				<>
					Are you sure you want to unset <strong>{title}</strong> as a demo project?
				</>
			),
		});

		if (userConfirmed) {
			try {
				await unsetDemoProject(uuid);
				window.location.reload();
			} catch (err) {
				console.error('Failed to unset demo project', err);
				addToast({
					id: 'demo-project-unset-error',
					bg: 'danger',
					title: 'Failed to unset demo project.',
				});
			}
		}
	};

	const handleDownloadImages = async () => {
		await downloadProjectFiles(uuid)
			.then(response => {
				addToast({
					id: 'download-images-success',
					bg: 'success',
					title: response,
				});
			})
			.catch(err => {
				addToast({
					id: 'download-images-error',
					bg: 'danger',
					title: err,
				});
			});
	};

	const presignedUrl = async (name, path, file_name) => {
		await adminGetProjectFilesDowloadUrl(uuid, path, file_name)
			.then(response => {
				const url = Object.values(response)[0]; // Get the first value from the response object
				window.open(url, '_blank'); // Open the URL in a new tab
			})
			.catch(err => {
				console.error('Failed getting project files for download', err);
				addToast({
					id: 'download-files-error',
					bg: 'danger',
					title: `Failed to get ${name}. Check the console for more information.`,
				});
			});
	};

	const menuItems = [
		{
			key: 'report',
			label: 'Resource report',
			icon: <AiOutlineFileText aria-hidden="true" />,
			onClick: () => setShowReport(true),
		},
		{
			key: 'view-collection-mode',
			label: 'View on map',
			icon: <FaRegMap aria-hidden="true" />,
			onClick: () => navigate(projectHomeUrl(uuid, image_mode, DEFAULT_PROJECT_VIEW_MODE)),
		},
		...(MODEL_VIEW_MODE_ENABLED && image_mode !== IMAGE_MODE.VIDEO
			? [
					{
						key: 'view-model-mode',
						label: 'View in model-based mode',
						icon: <AiOutlineHistory aria-hidden="true" />,
						onClick: () => navigate(buildProjectUrl(uuid, PROJECT_VIEW_MODE.MODEL)),
					},
				]
			: []),
		{
			key: 'details',
			label: 'Details',
			icon: <AiOutlineFileText aria-hidden="true" />,
			onClick: () => setShowDetails('project'),
		},
		{ type: 'divider', key: 'divider-general' },
		{
			key: 'edit-project',
			label: 'Edit project',
			icon: <AiOutlineFileText aria-hidden="true" />,
			onClick: () => setShowEditModal('project'),
		},
		{
			key: 'download-images',
			label: 'Images from s3',
			icon: <AiOutlineDownload aria-hidden="true" />,
			onClick: handleDownloadImages,
		},
		(image_mode === IMAGE_MODE.GENERATE_ORTHOPHOTO ||
			image_mode === IMAGE_MODE.UPLOAD_ORTHOPHOTO) && {
			key: 'odm-report',
			label: 'ODM report',
			icon: <AiOutlineFilePdf aria-hidden="true" />,
			onClick: () => presignedUrl('ODM report', 'odm_report/', 'report.pdf'),
		},
		(image_mode === IMAGE_MODE.GENERATE_ORTHOPHOTO ||
			image_mode === IMAGE_MODE.UPLOAD_ORTHOPHOTO) && {
			key: 'odm-log',
			label: 'ODM log',
			icon: <AiOutlineFileText aria-hidden="true" />,
			onClick: () => presignedUrl('ODM log', '', 'log.json'),
		},
		{
			key: 'sagemaker',
			label: 'Sagemaker files',
			icon: <AiOutlineFileText aria-hidden="true" />,
			onClick: () => setShowSagemaker(true),
		},
		{ type: 'divider', key: 'divider-demo' },
		{
			key: 'demo-project',
			label: demo_project ? 'Update demo model' : 'Set as demo project',
			icon: <AiOutlinePlayCircle aria-hidden="true" />,
			onClick: () => setShowProjectDemo(true),
		},
		demo_project && {
			key: 'unset-demo-project',
			label: 'Unset as demo project',
			icon: <AiOutlinePlayCircle aria-hidden="true" />,
			onClick: handleUnsetDemo,
		},
		{ type: 'divider', key: 'divider-danger' },
		image_mode === IMAGE_MODE.GENERATE_ORTHOPHOTO && {
			key: 'odm-postprocess',
			label: 'ODM Postprocess',
			icon: <AiOutlinePlayCircle aria-hidden="true" />,
			onClick: () => setShowPostprocess(true),
		},
		{
			key: 'archive',
			label: 'Archive',
			icon: <AiFillFolderOpen aria-hidden="true" />,
			onClick: handleDelete,
		},
	].filter(Boolean);

	return (
		<>
			<AntdDropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
				<AntdButton
					type="text"
					icon={<AiOutlineMore aria-hidden="true" />}
					aria-label={`Toggle actions for ${title}`}
				/>
			</AntdDropdown>

			{showReport && (
				<ReportModal project_uuid={uuid} show={showReport} setShow={setShowReport} />
			)}

			{showDetails && (
				<DetailsModal project_uuid={uuid} type={showDetails} setType={setShowDetails} />
			)}

			{showPostprocess && (
				<OdmPostprocessModal
					project_uuid={uuid}
					image_mode={image_mode}
					show={showPostprocess}
					setShow={setShowPostprocess}
				/>
			)}

			{showSagemaker && (
				<SagemakerDownloadModal
					project_uuid={uuid}
					show={showSagemaker}
					setShow={setShowSagemaker}
				/>
			)}

			{showProjectDemo && (
				<DemoProjectModal
					project={data}
					show={showProjectDemo}
					setShow={setShowProjectDemo}
				/>
			)}

			{showEditModal && (
				<EditModal project={data} show={showEditModal} setShow={setShowEditModal} />
			)}
		</>
	);
};
export default DropdownMenu;
